import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'

export type AuditExportJob = { id: string; status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED'; creditsCharged: number; createdAt: unknown }
export type AuditExportRow = Record<string, unknown> & { id: string; timestamp?: unknown; createdAt?: unknown }
export type AuditExportFilters = { from?: string | number | Date | null; to?: string | number | Date | null }
export type WorkspaceReportMetrics = {
  window: { from: string; to: string; days: number }
  totalActions: number
  activeMemberParticipation: Array<{ actorId: string; actorName: string; actions: number }>
  blockerDelayEventCount: number
  inventoryScanAccuracy: { successfulScans: number; errors: number; in: number; out: number; accuracy: number | null }
  velocity: { productiveMutations: number; productiveMutationsPerDay: number; byDay: Record<string, number> }
  actionTypeBreakdown: Record<string, number>
}
export type WorkspaceReportResult = { reportId: string; summary: string; metrics: WorkspaceReportMetrics; report: Record<string, unknown>; creditsCharged: number }

const toMillis = (value: unknown): number => {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (value && typeof value === 'object' && 'toMillis' in value && typeof value.toMillis === 'function') {
    const parsed = Number(value.toMillis())
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const boundaryMillis = (value: AuditExportFilters[keyof AuditExportFilters], fallback: number): number =>
  value == null ? fallback : toMillis(value)

const normalizeForJson = (value: unknown): unknown => {
  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value)) return value.map(normalizeForJson)
  if (value && typeof value === 'object') {
    if ('toDate' in value && typeof value.toDate === 'function') return value.toDate().toISOString()
    if ('toMillis' in value && typeof value.toMillis === 'function') return new Date(value.toMillis()).toISOString()
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, normalizeForJson(entry)]))
  }
  return value
}

export const buildAuditExportPayload = (
  rows: AuditExportRow[],
  filters: AuditExportFilters = {},
) => {
  const from = boundaryMillis(filters.from, Number.NEGATIVE_INFINITY)
  const to = boundaryMillis(filters.to, Number.POSITIVE_INFINITY)
  const exportedRows = rows
    .filter((row) => {
      const timestamp = toMillis(row.timestamp ?? row.createdAt)
      return timestamp >= from && timestamp <= to
    })
    .sort((first, second) => {
      const delta = toMillis(first.timestamp ?? first.createdAt) - toMillis(second.timestamp ?? second.createdAt)
      return delta || first.id.localeCompare(second.id)
    })
    .map((row) => normalizeForJson(row))
  return {
    filters: normalizeForJson(filters),
    rows: exportedRows,
    rowCount: exportedRows.length,
  }
}

export const createAuditExportJob = async (workspaceId: string, filters: Record<string, unknown>) => {
  const callable = httpsCallable<{ workspaceId: string; filters: Record<string, unknown> }, { jobId: string; status: 'QUEUED'; creditsCharged: number }>(firebaseFunctions, 'createAuditExportJob')
  return (await callable({ workspaceId, filters })).data
}

export const generateWorkspaceReport = async (workspaceId: string, filters: Record<string, unknown>, reservationId = crypto.randomUUID()) => {
  const callable = httpsCallable<{ workspaceId: string; filters: Record<string, unknown>; reservationId: string }, WorkspaceReportResult>(firebaseFunctions, 'generateWorkspaceReport')
  return (await callable({ workspaceId, filters, reservationId })).data
}

export const subscribeAuditExportJobs = (workspaceId: string, onChange: (jobs: AuditExportJob[]) => void, onError?: (error: Error) => void) => onSnapshot(
  query(collection(firestoreDb, 'audit_export_jobs'), where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'), limit(20)),
  (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as AuditExportJob)),
  (error) => onError?.(error),
)
